import { mutatorAction } from 'satcheljs';
import type { TraceErrorObject } from 'owa-analytics-types';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';
import { yieldIdle } from 'owa-task-queue/lib/schedule';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Transaction needed to fix performance problem from previous legacy actions */
import { transaction } from 'owa-transaction';

export interface ReferenceGetter {
    (): string | string[];
}

export default class MruCache<T> {
    private store: Map<string, T> | undefined;
    private deleteCallback: ((key: string) => void) | undefined;
    private references: ReferenceGetter[] = [];
    protected mruList: string[] = []; // sorted from older to newer

    constructor(
        private source: string,
        private maxSize: number
    ) {}

    isInitialized(): boolean {
        return this.store !== undefined;
    }

    initialize(store: Map<string, T>, deleteCallback?: (key: string) => void): void {
        if (!this.store) {
            this.store = store;
            this.deleteCallback = deleteCallback;

            // Initialize the MRU list with the existing contents of the cache
            for (const key of store.keys()) {
                this.mruList.push(key);
            }
        }
    }

    add(key: string, value: T, skipMutator?: boolean): void {
        addToTimingMap('mruCache', this.source);

        (skipMutator ? addToMruCacheInternal : addToMruCacheMutator)(this.store, key, value);

        if (this.mruList.indexOf(key) >= 0) {
            // The item is already in the cache, so just touch it
            this.touch(key);
        } else {
            // Add the item and, if necessary, purge old items
            this.mruList.push(key);
            this.purgeIfNecessary(skipMutator);
        }
    }

    get(key: string): T | undefined {
        return this.store?.get(key);
    }

    getAndTouch(key: string): T | undefined {
        const obj = this.store?.get(key);
        if (obj && this.mruList.indexOf(key) >= 0) {
            this.touch(key);
        }

        return obj;
    }

    has(key: string): boolean | undefined {
        return this.store?.has(key);
    }

    /** Returns whether the key currently participates in MRU recency tracking. */
    isInMru(key: string): boolean {
        return this.mruList.indexOf(key) >= 0;
    }

    remove(key: string): void {
        if (this.store?.has(key) && !this.isReferenced(key) && this.mruList.indexOf(key) >= 0) {
            return this.removeItemAtIndex(this.mruList.indexOf(key));
        }
    }

    registerReference(reference: ReferenceGetter): void {
        this.references.push(reference);
    }

    clear(): void {
        // Try purge all the items
        this.purgeTillSize(0);
    }

    /**
     * We return a new array instead of the mruList because we do not want the callee to update the mruList directly.
     * Also we assume that callee would want to do something with these ids present at a
     * particular time when the api is called and as mruList will be changing all the times,
     * callee would end up operating on the wrong set of the item ids if we returned mruList.
     */
    getItemIds(): string[] {
        return new Array(...this.mruList);
    }

    /**
     * Returns the keys currently in the cache that are NOT referenced (i.e. not pinned by any
     * registered reference, such as a conversation open in the reading pane). These are the
     * "cold" entries that are safe to compress or otherwise treat as evictable.
     */
    getUnreferencedItemIds(): string[] {
        return this.mruList.filter(key => !this.isReferenced(key));
    }

    /**
     * Checks if a key is referenced (i.e. pinned by any registered reference).
     */
    isReferenced(key: string): boolean {
        for (let i = 0; i < this.references.length; i++) {
            const referenceValue: string | string[] = this.references[i]();
            if (Array.isArray(referenceValue)) {
                if (referenceValue.indexOf(key) != -1) {
                    return true;
                }
            } else {
                if (referenceValue == key) {
                    return true;
                }
            }
        }

        return false;
    }

    private touch(key: string) {
        const i = this.mruList.indexOf(key);
        if (i < 0) {
            const err: TraceErrorObject = new Error('MruCache:InvalidKey');
            err.additionalInfo = { key };
            throw err;
        }

        // Move the key to the end of the list
        this.mruList.splice(i, 1);
        this.mruList.push(key);
    }

    /**
     * If we're above max MRU capacity, purge oldest items that are not referenced until we're under max size.
     */
    private purgeIfNecessary(skipMutator?: boolean) {
        this.purgeTillSize(this.maxSize, skipMutator);
    }

    private pendingPurge: boolean = false;

    /**
     * Purge items until mru list is below the given size
     */
    private async purgeTillSize(size: number, skipMutator?: boolean) {
        if (isGlobalFeatureEnabled('fwk-loaf-mru-cache')) {
            if (this.pendingPurge) {
                return;
            }

            this.pendingPurge = true;
            await yieldIdle(`${this.source}-MruCachePurge`);

            transaction(() => {
                this.purgeTillSizeInternal(size, skipMutator);
            });
            this.pendingPurge = false;
        } else {
            this.purgeTillSizeInternal(size, skipMutator);
        }
    }

    private purgeTillSizeInternal(size: number, skipMutator?: boolean) {
        let i = 0;
        while (this.mruList.length > size && i < this.mruList.length) {
            const key = this.mruList[i];
            if (this.isReferenced(key)) {
                i++;
            } else {
                this.removeItemAtIndex(i, skipMutator);
            }
        }
    }

    /**
     * Remove item from MRU at specified index
     */
    private removeItemAtIndex(index: number, skipMutator?: boolean) {
        if (index < 0 || index >= this.mruList.length) {
            const err: TraceErrorObject = new Error('MruCache: Invalid index to remove');
            err.additionalInfo = { index };
            throw err;
        }

        const key = this.mruList[index];
        this.mruList.splice(index, 1);

        if (this.deleteCallback) {
            this.deleteCallback(key);
        } else {
            (skipMutator ? deleteFromMruCacheInternal : deleteFromMruCacheMutator)(this.store, key);
        }
    }
}

const addToMruCacheMutator = mutatorAction('addToMruCache', addToMruCacheInternal);

function addToMruCacheInternal(store: Map<string, any> | undefined, key: string, value: any) {
    store?.set(key, value);
}

const deleteFromMruCacheMutator = mutatorAction('deleteFromMruCache', deleteFromMruCacheInternal);

function deleteFromMruCacheInternal(store: Map<string, any> | undefined, key: string) {
    store?.delete(key);
}
